import { POST } from "../../API/API";

const DEFAULT_BACKOFF_BASE_MS = 500;
const DEFAULT_BACKOFF_MAX_MS = 10_000;

const formatLanguageCode = (langObj) => {
  if (!langObj) return "en";
  
  let code = typeof langObj === 'string' 
    ? langObj 
    : (langObj.code || langObj.locale || langObj.language_code || "en"); 
  
  let cleanedCode = code.replace("-online", ""); 
  
  return cleanedCode.split('-')[0].toLowerCase(); 
};

const buildConfiguration = ({ languageA, languageB, ttsEnabled, pitch, speed }) => {
  return {
    translations_enabled: true,
    stt_enabled: true,
    tts_enabled: Boolean(ttsEnabled),
    language_a: formatLanguageCode(languageA),
    language_b: formatLanguageCode(languageB),
    tts_speed: speed !== undefined ? speed : 1.0
  };
};

const createConversationSession = async (configParams) => {
  const configuration = buildConfiguration(configParams);
  
  return new Promise((resolve, reject) => {
    POST(
      "/api/v1/connector/realtime/conversation",
      { configuration },
      (response) => {
        resolve(response?.data);
      },
      (error) => {
        reject(error);
      }
    );
  });
};

export default class RealtimeConversationClient {
  constructor({
    onOpen,
    onClose,
    onError,
    onTranslatedText,
    onRecognizedText,
    onConfigurationUpdated,
    onAudio,
  } = {}) {
    this.ws = null;
    this.isConnecting = false;

    this.onOpen = onOpen;
    this.onClose = onClose;
    this.onError = onError;
    this.onTranslatedText = onTranslatedText;
    this.onRecognizedText = onRecognizedText;
    this.onConfigurationUpdated = onConfigurationUpdated;
    this.onAudio = onAudio;

    this.backoffAttempt = 0;
    this.reconnectTimer = null;

    this.languageA = null;
    this.languageB = null;
    this.desiredTtsEnabled = false;

    this.speed = 1.0;
  }

  get isConnected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  async connect({ languageA, languageB, ttsEnabled, speed } = {}) {
    if (!languageA || !languageB) throw new Error("Both languageA and languageB are required.");
    this.languageA = languageA;
    this.languageB = languageB;
    this.desiredTtsEnabled = Boolean(ttsEnabled);
    if (speed !== undefined) this.speed = speed;

    if (this.isConnecting || this.isConnected) return;
    this.isConnecting = true;
    try {
      const session = await createConversationSession({
        languageA: this.languageA,
        languageB: this.languageB,
        ttsEnabled: this.desiredTtsEnabled,
        speed: this.speed,
      });
      const accessUrl = session?.access_url;
      if (!accessUrl) throw new Error("Missing access_url from backend");
      await this.connectWebSocket(accessUrl);
    } finally {
      this.isConnecting = false;
    }
  }

  async reconnect() {
    await this.disconnect();
    return this.connect({
      languageA: this.languageA,
      languageB: this.languageB,
      ttsEnabled: this.desiredTtsEnabled,
      speed: this.speed,
    });
  }

  scheduleReconnect({ baseMs = DEFAULT_BACKOFF_BASE_MS, maxMs = DEFAULT_BACKOFF_MAX_MS } = {}) {
    if (!this.languageA || !this.languageB) return;
    this.clearReconnectTimer();
    const delay = Math.min(maxMs, baseMs * 2 ** this.backoffAttempt);
    this.backoffAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnect().catch((err) => {
        if (this.onError) this.onError(err);
        this.scheduleReconnect({ baseMs, maxMs });
      });
    }, delay);
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  async disconnect() {
    this.clearReconnectTimer();
    if (this.ws) {
      try {
        this.ws.onopen = null;
        this.ws.onclose = null;
        this.ws.onerror = null;
        this.ws.onmessage = null;
        this.ws.close();
      } catch {}
      this.ws = null;
    }
    this.isConnecting = false;
  }

  async connectWebSocket(accessUrl) {
    await this.disconnect();
    return new Promise((resolve, reject) => {
      try {
        const ws = new WebSocket(accessUrl);
        ws.binaryType = "arraybuffer";
        this.ws = ws;
        ws.onopen = () => { if (this.onOpen) this.onOpen(); resolve(); };
        ws.onerror = (event) => {
          const err = event?.error || new Error("Websocket connection error");
          if (this.onError) this.onError(err);
          reject(err);
        };
        ws.onclose = () => { if (this.onClose) this.onClose(); };
        ws.onmessage = (event) => {
          const data = event?.data;
          if (typeof data === "string") { this.handleJsonMessage(data); return; }
          if (data instanceof ArrayBuffer) { if (this.onAudio) this.onAudio(data); return; }
          if (data instanceof Blob) {
            data.arrayBuffer().then((buf) => { if (this.onAudio) this.onAudio(buf); }).catch(() => {});
          }
        };
      } catch (err) { reject(err); }
    });
  }

  handleJsonMessage(raw) {
    let parsed;
    try { parsed = JSON.parse(raw); } catch { return; }
    if (parsed?.type === "text_translated" && this.onTranslatedText) { this.onTranslatedText(parsed); return; }
    if (parsed?.type === "text_recognized" && this.onRecognizedText) { this.onRecognizedText(parsed); return; }
    if (parsed?.type === "error" && this.onError) { this.onError(new Error(parsed?.message || parsed?.code || "API error")); }
  }

  sendJson(payload) {
    if (!this.isConnected) throw new Error("Websocket not connected");
    this.ws.send(JSON.stringify(payload));
  }

  sendAudio(arrayBuffer) {
    if (!arrayBuffer || arrayBuffer.byteLength === 0 || !this.isConnected) return;
    this.ws.send(arrayBuffer);
  }

  async updateConfiguration(configParams = {}) {
    if (!this.isConnected) return;
    if (configParams.languageA) this.languageA = configParams.languageA;
    if (configParams.languageB) this.languageB = configParams.languageB;
    if (configParams.ttsEnabled !== undefined) this.desiredTtsEnabled = Boolean(configParams.ttsEnabled);
    if (configParams.speed !== undefined) this.speed = configParams.speed;

    const configuration = buildConfiguration({
      languageA: this.languageA,
      languageB: this.languageB,
      ttsEnabled: this.desiredTtsEnabled,
      speed: this.speed,
    });

    this.sendJson({ type: "update_configuration", configuration });
  }
}